import type { Item as _Item, Menu as _Menu, Variant } from "$lib/serv/restaurant";
import type { Subject, ValueFromArray } from "rxjs";
import type { Cart } from "$lib/def/food-cart";
import type { SelectedEntity as _SelectedEntity, Item, Section } from "proto/menu";
import type { CartItem as _CartItem, ChoiceGroup, FoodMenuChoiceIssueReport, WithOptionsAndExtras } from "$lib/def/food-menu";

import { addItem, isolateCartItemSelection } from "$lib/serv/food-cart";
import { toastError, toastSuccess } from "$lib/serv/toast";
import { extractErrorMessage } from "$lib/util/format";

export type MenuItem = _Item & { restaurant_id: number, menu_id: number, section_id: number };

export type SelectedEntity = _SelectedEntity & { menu_id?: number };

export type Menu = _Menu & { selected_entity?: SelectedEntity };
export type CartItem = _CartItem & { index: number | undefined };

export const DIVIDER = '€';

// export function getMenuItem(comboSlug: string): Observable<MenuItem | null> {
//   return of(comboSlug).pipe(
//     switchMap((comboSlug) => {
//       console.log(comboSlug);
//       const [item, sectionId, menuId, restId] = comboSlug.split(DIVIDER);
      
//       const slugFetch = Number.isNaN(Number(item));
//       if (slugFetch) {
//         const encodedSlug = encodeURIComponent(item);
//         return get$<Menu>(`api://v2/menus/${menuId}?flattened=true&slug=${encodedSlug}`).pipe(
//           map((menu) => {
//             menu.selected_entity.menu_id = +menuId;
//             return menu;
//           })
//         );
//       } 


type Id = string | number;
export function makeRestPDPSlug(itemId: Id, sectionId: Id, menuId: Id, restaurantId: Id): string {
  return [itemId, sectionId, menuId, restaurantId].join(DIVIDER);
}


export async function getMenuItem(normalizedMenu: Menu, hash?: string | null): Promise<MenuItem | null> {
  if (!normalizedMenu) return null;
  const [itemId, sectionId, menuId, restaurantId] = hash?.split(DIVIDER) ?? [];
  const slugFetch = Number.isNaN(Number(itemId));

  if (!slugFetch) {
    (<any>normalizedMenu).selected_entity = {
      item_id: +itemId,
      section_id: sectionId === '' ? undefined : +sectionId,
      menu_id: +menuId,
    }
  }

  // const normalMenu = normalise(menu);
  const finalMenu = {...normalizedMenu, selected_entity: normalizedMenu.selected_entity} as Menu;
  const { item_id, section_id, menu_id } = finalMenu.selected_entity || {};

  let section: Section | undefined;
  let item: Item | undefined;
  if (typeof section_id === 'number') {
    section = finalMenu.sections.find((s) => s.id === section_id);
    if (!section) {
      console.error("Couldn't get menu section id", section_id);
      throw new Error(`Section not found. (${section_id})`);
    }
  
    item = section.items.find((i) => i.id === item_id);
  } else {
    finalMenu.sections.forEach(({items}) => items.forEach(i => {
      if (!item && i.id === item_id) item = i;
    }))
  }
  if (!item) {
    console.error("Couldn't get menu item id", item_id);
    throw new Error(`Item not found. (${item_id})`);
  }

  const bogofItem = isBOGOF(finalMenu, item as MenuItem);
  if (bogofItem) {
    item.menu_deal_action = bogofItem.menu_deal_action;
  }

  if (section_id || section) (<MenuItem>item).section_id = section_id || section!.id;
  (<MenuItem>item).menu_id = menu_id || normalizedMenu.id;
  (<MenuItem>item).restaurant_id = finalMenu.has_menu_id;

  return item as MenuItem;
}

function isBOGOF(menu: Menu, item: MenuItem): Item | null {
  for (const s of menu.sections) {
    for (const i of s.items) {
      if (i.name === item.name && i.menu_deal_action === "buy_1_get_1_free") {
        return i;
      }
    }
  }
  return null;
}

export function getCartItem(menuItem: MenuItem, cart?: Cart | null, index?: number): CartItem {
  const defaultItem = {
    section_id: menuItem.section_id,
    item_id: menuItem.id,
    variant_id: getSelectedVariant(menuItem.variants).id,
		quantity: 1,
    index,
    options: [],
    extras: [],
    addons: []
  } as CartItem;

  if (!cart) {
    return defaultItem;
  }

	const full = typeof index === 'number' ? cart.items[index as number] : defaultItem;
		// it helps to isolate the selection data so that the server does not complain when we send fluff
  return {...isolateCartItemSelection(full as any), was: (full as any).price, index } as CartItem;
}

export function getSelectedVariant(variants: Variant[], variantId?: number): Variant {
  // console.log('00000000000000000', variants, variantId);
  return variants.find(v => v.id === variantId)
    ?? variants.find(v => v.default)
    ?? variants[0];
}

export type SelectionValue = ChoiceGroup[] | ChoiceGroup<WithOptionsAndExtras>[];

export async function updateCart(menuItem: MenuItem, cartItem: CartItem, reporter: Subject<FoodMenuChoiceIssueReport>, onCartUpdated?: () => void) {
	let didFocusOnError = false;
	let canContinue = true;
	reporter.next({highlight: true, report: (id, issues) => {
		if (issues?.length) {
			if (!didFocusOnError) {
				const el = document.getElementById(id);
				if (el) {
					el.scrollIntoView({ behavior: 'smooth' });
					didFocusOnError = true;
				}
			}
			canContinue = false;
		}
	}});
	if (canContinue) {
		try {
			// busy = true;
			const cart = await addItem({ restaurant_id: menuItem.restaurant_id, menu_id: menuItem.menu_id, item: cartItem, editIndex: cartItem.index })
			toastSuccess('Item updated.', true);
      if (onCartUpdated) onCartUpdated();
		} catch (er) {
			toastError(extractErrorMessage(er, 'Unable to add this item to your cart. Please try again.'), true);
		} finally {
			// busy = false;
		}
	}
}

export function getMenuDealLabel(deal: string): string {
  switch(deal) {
    case "buy_1_get_1_free":
    return "Buy 1 Get 1 Free";

    case "discount":
    return "Discount";

    default:
    return deal;
  }
}

//####################
// DEBUG
//####################

// IDEAS
// 1. find item with same name but no menu_deal_action prop, and use that id to populate bogof pdp.

export function logMenuItem(menu: Menu, itemName: string) {
  menu.sections.forEach((section) => {
    section.items.forEach((item) => {
      if (item.name === itemName) {
        console.log(item.menu_deal_action);
        const x = {
          section_id: section.id,
          section_name: section.name,
          item
        };
        // console.log(`section_id = ${section.id}`);
        // console.log(`section_name = ${section.name}`);
        // console.log(`item_id = ${item.id}`);
        console.log(JSON.stringify(x) + "\n");
      }
    });
  });
}

function mapDealActions(menu: Menu): Map<string, number> {
  const map = new Map<string, number>();
   for (const s of menu.sections) {
    for (const i of s.items) {
      if (i.menu_deal_action) {
        const count = (map.get(i.menu_deal_action) ?? 0) + 1;
        map.set(i.menu_deal_action, count)
      }
    }
  }
  return map;
}

function printDealActions(map: Map<string, number>) {
  for (const key of map.keys()) {
    console.log(`${key} => ${map.get(key)}`);
  }
}
